import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CellProps } from 'react-table';
import { toast } from 'react-toastify';
import momentTimezone from 'moment-timezone';
import {
  getEmailsByFlyerGroup,
  getFlyerStatuses,
  getFlyerTemplates,
  patchFlyer,
  postFlyersOnsite
} from '~/api/gatherings';
import { getFlyerNotes } from '~/api/notes';
import ActionDropDown from '~/components/ActionDropDown/ActionDropDown';
import TextDelimeter from '~/components/TextDelimeter/TextDelimeter';
import { ISelectOption } from '~/helpers/interfaces';
import { IEventWithFlyer, IGatheringFlyer } from '~/types/gathering';
import { IEmailFlyerFormData } from '~/containers/FlyerManagement/FlyerTable/EmailFylerForm/EmailFlyerForm';
import { cloneDeep } from 'lodash';
import { flyerTemplateOptions } from '~/containers/FlyerManagement/FlyerTable/EmailFylerForm/constants';
import html2canvas from 'html2canvas';
import Axios from 'axios';
import Spacer from '~/components/Spacer/Spacer';
import { saveAs } from 'file-saver';
import { ReactComponent as ChainLink } from '~/utils/images/link.svg';
import { qrCodeGeneratorURL } from '~/helpers/constants';

interface UseFlyerTableDataParams {
  eventsWithFlyer: IEventWithFlyer[];
  refetchFlyers: () => void;
  completed: boolean;
  updateItemInCache: (
    eventUuid: string,
    updater: (item: IEventWithFlyer) => IEventWithFlyer
  ) => void;
  removeItemFromCache: (eventUuid: string) => void;
}

export default ({
  eventsWithFlyer,
  refetchFlyers,
  completed,
  updateItemInCache,
  removeItemFromCache,
}: UseFlyerTableDataParams) => {
  const [editFlyerData, setEditFlyerData] = useState<{
    flyer: IGatheringFlyer | null;
    event: IEventWithFlyer | null;
  }>({
    flyer: null,
    event: null
  });
  const [loading, setLoading] = useState(false);

  const [emailFlyerLoader, setEmailFlyerLoader] = useState(false);
  const [emailFlyerData, setEmailFlyerData] = useState<{
    event: IEventWithFlyer | null;
    flyerGroupEmails: string[] | null;
  }>({
    event: null,
    flyerGroupEmails: null
  });
  const [flyerTemplatOptions, setFlyerTemplateOptions] =
    useState<ISelectOption[]>();

  const pdfIFrameRef = useRef<HTMLIFrameElement>();
  const instaIFrameRef = useRef<HTMLIFrameElement>();

  const [templateSelectionOption, setTemplateSelectionOption] = useState(
    cloneDeep(flyerTemplateOptions)
  );

  const [flyerStatusOptions, setFlyerStatusOptions] =
    useState<ISelectOption[]>();

  const [pdfFlyerHtmlContent, setPdfFlyerHtmlContent] = useState('');
  const [instaFlyerHtmlContent, setInstaFlyerHtmlContent] = useState('');
  const [byIdflyerGroupEmails, setByIdflyerGroupEmails] = useState(null);
  const [byBuildingIdFlyerNotes, setByBuildingIdFlyerNotes] = useState<
    Record<string, any[]>
  >({});

  // Track which building IDs we've already fetched notes for to avoid duplicate requests
  const fetchedNotesBuildingIdsRef = useRef<Set<string>>(new Set());

  const history = useHistory();

  const populateFlyerDataInHtml = (
    htmlString: string,
    flyer: IGatheringFlyer
  ): string => {
    const {
      flyerBgImage,
      flyerHeading,
      flyerSubHeading,
      flyerDescription,
      flyerLogo,
      flyerDetails: {
        footer = '',
        mobileStoreLink = '',
        propertyCode = '',
        propertyQrImage = ''
      }
    } = flyer;

    const dataToPopulate = {
      flyerBgImage,
      flyerHeading: flyerHeading.toUpperCase(),
      flyerSubHeading,
      flyerDescription,
      flyerLogo,
      footer,
      mobileStoreLink,
      propertyCode,
      propertyQrImage
    };

    Object.entries(dataToPopulate).forEach(([key, value]) => {
      htmlString = htmlString.replace(`$${key}`, value);
    });

    return htmlString;
  };

  const fetchFlyerTemplates = async (flyerTemplateType: string) => {
    try {
      const res = await getFlyerTemplates(flyerTemplateType);

      setFlyerTemplateOptions(
        res.data.map((template) => ({
          label: template.name,
          value: template.uuid
        }))
      );
    } catch (e) {
      toast(e.message || 'Failed to fetch templates');
    }
  };

  const downloadAttachment = async (
    isPdf: boolean,
    htmlLocation: string,
    flyer: IGatheringFlyer
  ) => {
    try {
      setLoading(true);

      const fileName = `${flyer.buildingName} - ${
        flyer.flyerEventName
      } - ${moment(flyer.flyerEventDate).format('MM-DD-YYYY')}`;

      const res = await Axios.get(htmlLocation);
      const populatedHtml = populateFlyerDataInHtml(res.data, flyer);

      isPdf
        ? setPdfFlyerHtmlContent(populatedHtml)
        : setInstaFlyerHtmlContent(populatedHtml);

      setTimeout(() => {
        isPdf ? printIframe(fileName) : screenshotIframe(true, fileName);
        setLoading(false);
      }, 1000);
    } catch (e) {
      setLoading(false);
      toast(e.message || 'Failed to download attachment');
    }
  };

  // ── Optimistic status change (no full refetch) ──
  const onChangeFlyerStatus = useCallback(
    async (
      eventUuid: string,
      flyers: IGatheringFlyer[],
      newStatusUuid: string
    ) => {
      // Resolve the new status name from our options
      const newStatusOption = flyerStatusOptions?.find(
        (o) => o.value === newStatusUuid
      );
      const newStatusName = newStatusOption?.label || '';
      const newStatusColor =
        newStatusOption ? '' : ''; // color comes from server, keep existing

      // Determine if this change crosses the completed boundary
      const isMovingToCompleted =
        newStatusName.toLowerCase().trim() === 'completed';
      const shouldRemoveFromView =
        (isMovingToCompleted && !completed) ||
        (!isMovingToCompleted && completed);

      // Optimistic update: immediately reflect in UI
      if (shouldRemoveFromView) {
        removeItemFromCache(eventUuid);
      } else {
        updateItemInCache(eventUuid, (item) => ({
          ...item,
          flyerStatus: newStatusName as any,
          flyerStatusUuid: newStatusUuid as any,
          flyers: item.flyers.map((f) => ({
            ...f,
            flyerStatus: newStatusName as any,
            flyerStatusUuid: newStatusUuid,
          })),
        }));
      }

      // Fire API call in background — call patchFlyer directly to avoid
      // per-flyer toast from useEditFlyer
      try {
        await Promise.allSettled(
          flyers.map(async (flyer) => {
            const formData = new FormData();
            formData.append('flyerUuid', flyer.flyerUuid);
            formData.append('flyerStatus', newStatusUuid);
            await patchFlyer(formData);
          })
        );
        toast('Flyer Updated Successfully');
      } catch (e) {
        // Revert on failure by refetching
        toast(e.message || 'Failed to change flyer status');
        refetchFlyers();
      }
    },
    [
      flyerStatusOptions,
      completed,
      removeItemFromCache,
      updateItemInCache,
      refetchFlyers,
    ]
  );

  const fetchFlyerStatuses = async () => {
    try {
      const { data: statuses } = await getFlyerStatuses();

      const options = statuses.map((status) => {
        return { label: status.name, value: status.uuid };
      });

      setFlyerStatusOptions(options);
    } catch (e) {
      toast(e.message || 'Failed to fetch flyer statuses');
    }
  };

  const fetchEmailsByFlyerGroup = async () => {
    try {
      setLoading(true);
      const res = await getEmailsByFlyerGroup();
      setByIdflyerGroupEmails(res.data);
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  };

  // Deduplicated notes fetching — only fetch for building IDs we haven't already fetched
  const fetchFlyerNotesForBuildings = useCallback(
    async (buildingIds: string[]) => {
      const newIds = buildingIds.filter(
        (id) => !fetchedNotesBuildingIdsRef.current.has(id)
      );
      if (newIds.length === 0) return;

      try {
        const notesMap: Record<string, any[]> = {};

        await Promise.all(
          newIds.map(async (buildingId) => {
            try {
              const res = await getFlyerNotes(buildingId);
              notesMap[buildingId] = res.data.rows || [];
            } catch (e) {
              notesMap[buildingId] = [];
            }
          })
        );

        // Mark as fetched
        newIds.forEach((id) => fetchedNotesBuildingIdsRef.current.add(id));

        // Merge with existing notes (don't overwrite already-fetched buildings)
        setByBuildingIdFlyerNotes((prev) => ({ ...prev, ...notesMap }));
      } catch (e) {
        console.error('Error fetching flyer notes:', e);
      }
    },
    []
  );

  useEffect(() => {
    fetchEmailsByFlyerGroup();
    fetchFlyerStatuses();
  }, []);

  useEffect(() => {
    if (eventsWithFlyer && eventsWithFlyer.length > 0) {
      const uniqueBuildingIds = Array.from(
        new Set(eventsWithFlyer.map((event) => event.buildingUuid))
      );
      fetchFlyerNotesForBuildings(uniqueBuildingIds);
    }
  }, [eventsWithFlyer, fetchFlyerNotesForBuildings]);

  const navigateToBuildingSetting = useCallback(
    (buildingId: string) => {
      history.push(`/building/${buildingId}/edit/emails`);
    },
    [history]
  );

  const openEmailModal = useCallback(
    (event: IEventWithFlyer, flyerGroupEmails: null | string[]) => {
      setEmailFlyerData({ event, flyerGroupEmails });
    },
    []
  );

  const printIframe = (fileName: string) => {
    const prevTitle = window.parent.document.title;
    document.title = fileName;
    const iframeWindow = pdfIFrameRef?.current?.contentWindow;
    if (!iframeWindow) {
      toast('Unable to print!');
      document.title = prevTitle;
      return;
    }
    iframeWindow?.print();
    document.title = prevTitle;
    return false;
  };

  const screenshotIframe = (download = true, fileName: string) => {
    const iframeWindow = instaIFrameRef?.current?.contentWindow;
    if (!iframeWindow?.document?.body) {
      toast('Unable to capture screenshot!');
      return;
    }
    html2canvas(iframeWindow?.document?.body as HTMLElement, {
      allowTaint: true,
      useCORS: true
    }).then(function (canvas) {
      var imgData = canvas.toDataURL();
      if (download) {
        var downloadLink = document.createElement('a');
        downloadLink.href = imgData;
        downloadLink.download = `${fileName}.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      } else {
        return imgData;
      }
    });
  };

  const downloadAttachments = useCallback(
    (flyers: IGatheringFlyer[], includeInstagram: boolean) => {
      (includeInstagram
        ? flyers
        : flyers.filter((flyer) => flyer.flyerTemplateType === 'PDF Flyer')
      ).forEach(async (flyer: IGatheringFlyer) => {
        const { flyerTemplateType, flyerHtmlLocation } = flyer;
        const isPdf = flyerTemplateType === 'PDF Flyer';
        await downloadAttachment(isPdf, flyerHtmlLocation, flyer);
      });
    },
    []
  );

  // ── Optimistic email submit (sets status to completed without full refetch) ──
  const onEmailFlyerSubmit = useCallback(
    async (
      flyerEmails: string,
      flyers: IGatheringFlyer[],
      flyerForm: IEmailFlyerFormData
    ) => {
      const { pdfFile, instaFile } = flyerForm;

      try {
        setEmailFlyerLoader(true);

        const formData = new FormData();
        formData.append('emails', flyerEmails);
        formData.append('content', flyerForm.description);
        pdfFile && formData.append('pdfFile', flyerForm.pdfFile as Blob);
        instaFile &&
          formData.append('instagramImage', flyerForm.instaFile as Blob);

        await postFlyersOnsite(formData);

        // Update flyer status to "completed" after successfully sending emails
        const completedStatus = flyerStatusOptions?.find(
          (option) => option.label.toLowerCase().trim() === 'completed'
        );

        if (completedStatus && flyers.length > 0) {
          await Promise.allSettled(
            flyers.map(async (flyer) => {
              const formData = new FormData();
              formData.append('flyerUuid', flyer.flyerUuid);
              formData.append('flyerStatus', completedStatus.value);
              await patchFlyer(formData);
            })
          );
        }

        setEmailFlyerLoader(false);
        onEmailModalCancel();

        // Optimistic cache update instead of full refetch
        const eventUuid = emailFlyerData.event?.uuid;
        if (eventUuid && completedStatus) {
          if (!completed) {
            // Viewing non-completed → this event just became completed → remove it
            removeItemFromCache(eventUuid);
          } else {
            // Viewing completed → update status in place
            updateItemInCache(eventUuid, (item) => ({
              ...item,
              flyerStatus: 'completed' as any,
              flyerStatusUuid: completedStatus.value as any,
              flyers: item.flyers.map((f) => ({
                ...f,
                flyerStatus: 'completed' as any,
                flyerStatusUuid: completedStatus.value,
              })),
            }));
          }
        }

        toast('Flyers sent to flyer recipients');
      } catch (e) {
        toast(e.message || 'Failed to push flyers on-site. Try again');
        setEmailFlyerLoader(false);
      }
    },
    [
      flyerStatusOptions,
      completed,
      emailFlyerData.event,
      removeItemFromCache,
      updateItemInCache,
    ]
  );

  const onEmailModalCancel = useCallback(() => {
    setEmailFlyerData({ event: null, flyerGroupEmails: null });
  }, []);

  const showFlyerEditModal = useCallback(
    (flyer: IGatheringFlyer, event: IEventWithFlyer) => {
      fetchFlyerTemplates(flyer.flyerTemplateType);
      setEditFlyerData({ flyer, event });
    },
    []
  );

  const onEditFlyerCancel = useCallback(() => {
    setEditFlyerData({ flyer: null, event: null });
  }, []);

  // ── Memoized columns (only recreated when their data dependencies change) ──
  const columns = useMemo(
    () => [
      {
        Header: 'BUILDING',
        accessor: 'buildingName',
        Cell: (cell: CellProps<IEventWithFlyer>) => (
          <TextDelimeter
            textElement={cell.value}
            maxDelimiter={100}
            moreMessage='See more'
            lessMessage='See less'
            contentStyle={{
              fontSize: '18px',
              color: 'rgb(39, 33, 99)',
              fontWeight: 500
            }}
          />
        )
      },
      {
        Header: 'CREATE DATE',
        accessor: 'createdAt',
        Cell: (cell: CellProps<IEventWithFlyer>) => {
          return (
            <div>
              {momentTimezone
                .tz(cell.value, cell.row.original.buildingTimezone)
                .format('MM/DD/YYYY')}
            </div>
          );
        }
      },
      {
        Header: 'DATE OF EVENT',
        accessor: 'startTime',
        Cell: (cell: CellProps<IEventWithFlyer>) => {
          return (
            <div>
              {momentTimezone
                .tz(cell.value, cell.row.original.buildingTimezone)
                .format('MM/DD/YYYY')}
            </div>
          );
        }
      },
      {
        Header: 'TIME OF EVENT',
        accessor: '',
        Cell: (cell: CellProps<IEventWithFlyer>) => {
          return (
            <div>
              {momentTimezone
                .tz(cell.row.original.startTime, cell.row.original.buildingTimezone)
                .format('hh:mm A')}
            </div>
          );
        }
      },
      {
        Header: 'LOCATION',
        accessor: 'locationString'
      },
      {
        Header: 'EVENT TITLE',
        accessor: 'title',
        Cell: (cell: CellProps<IEventWithFlyer>) => (
          <TextDelimeter
            textElement={cell.value}
            maxDelimiter={100}
            moreMessage='See more'
            lessMessage='See less'
            contentStyle={{
              fontSize: '18px',
              color: 'rgb(39, 33, 99)',
              fontWeight: 500
            }}
          />
        )
      },
      {
        Header: 'EVENT DESCRIPTION',
        accessor: 'description',
        Cell: (cell: CellProps<IEventWithFlyer>) => (
          <TextDelimeter
            textElement={cell.row.original.description}
            maxDelimiter={100}
            moreMessage='See more'
            lessMessage='See less'
            contentStyle={{
              fontSize: '18px',
              color: 'rgb(39, 33, 99)',
              fontWeight: 500
            }}
          />
        )
      },
      {
        Header: 'CREATOR NAME',
        accessor: 'creatorName'
      },
      {
        Header: 'CREATOR EMAIL',
        accessor: 'creatorEmail',
        Cell: (cell: CellProps<IEventWithFlyer>) => (
          <TextDelimeter
            textElement={cell.value}
            maxDelimiter={20}
            moreMessage='See more'
            lessMessage='See less'
            contentStyle={{
              fontSize: '18px',
              color: 'rgb(39, 33, 99)',
              fontWeight: 500
            }}
          />
        )
      },
      {
        Header: 'RSVP',
        accessor: 'isRSVP',
        Cell: (cell: CellProps<IEventWithFlyer>) => (
          <div>{cell.row.original.isRSVP ? 'true' : 'false'}</div>
        )
      },
      {
        Header: 'LEFT SPOTS',
        accessor: 'leftSpots',
        Cell: (cell: CellProps<IEventWithFlyer>) => (
          <div>{cell.value ?? '--'}</div>
        )
      },
      {
        Header: 'STATUS',
        accessor: 'flyerStatus',
        Cell: (cell: CellProps<IEventWithFlyer>) => {
          return (
            <select
              value={cell.row.original.flyerStatusUuid}
              onChange={(event) =>
                onChangeFlyerStatus(
                  cell.row.original.uuid,
                  cell.row.original.flyers,
                  event.target.value
                )
              }
            >
              {flyerStatusOptions?.map((option) => (
                <option
                  key={option.value}
                  label={option.label}
                  value={option.value}
                >
                  {option.value}
                </option>
              ))}
            </select>
          );
        }
      },
      {
        Header: 'BUILDING CODE',
        accessor: '',
        Cell: (cell: CellProps<IEventWithFlyer>) => {
          const firstFlyer = cell.row.original.flyers?.[0];
          return <div>{firstFlyer?.flyerDetails?.propertyCode || '--'}</div>;
        }
      },
      {
        Header: 'BUILDING LOGO',
        accessor: '',
        Cell: (cell: CellProps<IEventWithFlyer>) => {
          const src =
            (cell.row.original.propertyLogoFlyerUrl as string | undefined) ||
            (cell.row.original.propertyLogoUrl as string | undefined) ||
            cell.row.original.flyers?.[0]?.flyerLogo;

          return src ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center'
              }}
            >
              <img
                src={src}
                alt='Building logo'
                style={{ width: 60, height: 60, borderRadius: 8 }}
              />
              <Spacer width={5} />
              <div
                style={{
                  justifyContent: 'center',
                  alignContent: 'center',
                  width: 50,
                  cursor: 'pointer'
                }}
                onClick={() => {
                  toast('Downloading Logo....');
                  saveAs(src as string, 'building-logo.jpg');
                }}
              >
                <ChainLink className='margin-right-8' />
              </div>
            </div>
          ) : (
            <div>--</div>
          );
        }
      },
      {
        Header: 'QR CODE',
        accessor: '',
        Cell: (cell: CellProps<IEventWithFlyer>) => {
          const onboardingLink = cell.row.original.onboardingDeeplink;
          const generated = onboardingLink
            ? qrCodeGeneratorURL(encodeURIComponent(onboardingLink))
            : undefined;

          return generated ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center'
              }}
            >
              <img
                src={generated}
                alt='QR code'
                style={{ width: 60, height: 60 }}
              />
              <Spacer width={5} />
              <div
                style={{
                  justifyContent: 'center',
                  alignContent: 'center',
                  width: 50,
                  cursor: 'pointer'
                }}
                onClick={() => {
                  toast('Downloading QR....');
                  saveAs(generated as string, 'qr-code.jpg');
                }}
              >
                <ChainLink className='margin-right-8' />
              </div>
            </div>
          ) : (
            <div>--</div>
          );
        }
      },
      {
        Header: 'FLYER EMAILS',
        accessor: 'flyerEmails',
        Cell: (cell: CellProps<IEventWithFlyer>) => {
          let flyerEmails = '';

          const flyerGroupEmails =
            byIdflyerGroupEmails &&
            byIdflyerGroupEmails[cell.row.original.buildingUuid];

        if (flyerGroupEmails) {
          (flyerGroupEmails as string[]).forEach((email) => {
            flyerEmails = flyerEmails ? `${flyerEmails},${email}` : email;
          });
        }

          return (
            <TextDelimeter
              textElement={flyerEmails ? `${flyerEmails}` : 'Add Emails'}
              maxDelimiter={15}
              contentStyle={{
                color: 'rgb(39, 33, 99)',
                fontWeight: 500
              }}
              moreMessage='See more'
              lessMessage='See less'
              enableButton
              onClick={() => {
                if (flyerEmails) {
                  openEmailModal(cell.row.original, flyerGroupEmails);
                  return;
                }
                navigateToBuildingSetting(cell.row.original.buildingUuid);
              }}
            />
          );
        }
      },
      {
        Header: 'NOTES',
        accessor: (row: IEventWithFlyer) => {
          const buildingNotes = byBuildingIdFlyerNotes[row.buildingUuid] || [];
          const pinnedNotes = buildingNotes.filter(
            (note: any) => note.pinned
          );
          return pinnedNotes;
        },
        Cell: (cell: CellProps<IEventWithFlyer>) => {
          const notes = cell.value;
          return notes && notes.length > 0 ? (
            <div style={{ whiteSpace: 'normal', maxWidth: '400px' }}>
              {notes.map((note: any, index: number) => (
                <div
                  key={index}
                  style={{
                    marginBottom: index < notes.length - 1 ? '8px' : '0'
                  }}
                >
                  - {note.note}
                </div>
              ))}
            </div>
          ) : (
            <div>--</div>
          );
        }
      },
      {
        Header: 'ACTIONS',
        id: 'ACTIONS',
        Cell: (cell: CellProps<IEventWithFlyer>) => {
          let flyerEmails = '';

          const flyerGroupEmails =
            byIdflyerGroupEmails &&
            byIdflyerGroupEmails[cell.row.original.buildingUuid];

        if (flyerGroupEmails) {
          (flyerGroupEmails as string[]).forEach((email) => {
            flyerEmails = flyerEmails ? `${flyerEmails},${email}` : email;
          });
        }

          const content = cloneDeep(cell.row.original.flyers)
            .sort((a, b) => {
              if (a.flyerTemplateType < b.flyerTemplateType) return 1;
              if (a.flyerTemplateType > b.flyerTemplateType) return -1;
              return 0;
            })
            .map((flyer) => {
              const { flyerTemplateType, flyerHtmlLocation, flyerUuid } = flyer;
              const isPdf = flyerTemplateType === 'PDF Flyer';

              const option = (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '10px 10px'
                  }}
                  key={flyerUuid}
                >
                  <div
                    onClick={() =>
                      downloadAttachment(isPdf, flyerHtmlLocation, flyer)
                    }
                    className='more-actions-btn pointer'
                  >{`Download ${isPdf ? 'PDF' : 'Instagram pic'}`}</div>
                  <div
                    onClick={() =>
                      showFlyerEditModal(flyer, cell.row.original)
                    }
                    className='more-actions-btn pointer'
                  >{`Edit ${isPdf ? 'PDF' : 'Instagram'} flyer`}</div>
                  <TextDelimeter
                    textElement={
                      flyerEmails ? `${flyerEmails}` : 'Add Emails'
                    }
                    maxDelimiter={15}
                    contentStyle={{
                      color: 'rgb(39, 33, 99)',
                      fontWeight: 500
                    }}
                    moreMessage='See more'
                    lessMessage='See less'
                    enableButton
                    onClick={() => {
                      if (flyerEmails) {
                        openEmailModal(cell.row.original, flyerGroupEmails);
                        return;
                      }
                      navigateToBuildingSetting(flyer.flyerBuilding);
                    }}
                  />
                </div>
              );

              return (
                <ActionDropDown
                  title={isPdf ? 'PDF' : 'INSTAGRAM'}
                  containerStyles={{ margin: '0 5px' }}
                  optionStyles={{ width: 'unset', right: '50px' }}
                  showActionPointer={false}
                  containerClassCb={(isSelected) =>
                    isSelected
                      ? 'more-actions-btn more-actions-btn-selected'
                      : 'more-actions-btn more-actions-btn-unselected'
                  }
                  options={[{ content: option }]}
                />
              );
            });

          return <div style={{ display: 'flex' }}>{content}</div>;
        }
      }
    ],
    [
      flyerStatusOptions,
      byIdflyerGroupEmails,
      byBuildingIdFlyerNotes,
      onChangeFlyerStatus,
      openEmailModal,
      navigateToBuildingSetting,
      showFlyerEditModal,
    ]
  );

  return {
    columns,
    editFlyerData,
    onEditFlyerCancel,
    emailFlyerLoader,
    emailFlyerData,
    onEmailFlyerSubmit,
    onEmailModalCancel,
    flyerTemplatOptions,
    templateSelectionOption,
    setTemplateSelectionOption,
    pdfFlyerHtmlContent,
    instaFlyerHtmlContent,
    pdfIFrameRef,
    instaIFrameRef,
    loading,
    downloadAttachments,
    refetchFlyers,
    byIdflyerGroupEmails,
    byBuildingIdFlyerNotes
  };
};
